
// chatReducer.js
import { chatConstants, messageConstants } from "../actions/constants";

const timeOf = (value) => {
    const time = value ? new Date(value).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
};

/**
 * Список с сервера + то, что пришло по живому соединению, пока он грузился.
 * Раньше ответ просто заменял список, и сообщение, пришедшее за эту секунду,
 * пропадало из превью до следующего обновления. Берём более свежее из двух.
 * Открытый чат прочитан, даже если отметка ещё не дошла до сервера.
 */
const mergeLoadedChats = (current = [], loaded = [], activeChatId = null) => {
    const local = new Map((current || []).map((chat) => [String(chat?._id || ""), chat]));
    const activeId = String(activeChatId || "");

    return (loaded || []).map((serverChat) => {
        const chatId = String(serverChat?._id || "");
        const mine = local.get(chatId);
        let next = serverChat;

        if (mine && timeOf(mine.lastMessageAt) > timeOf(serverChat.lastMessageAt)) {
            next = {
                ...serverChat,
                lastMessage: mine.lastMessage,
                lastMessageAt: mine.lastMessageAt,
                updatedAt: mine.updatedAt || serverChat.updatedAt,
                unreadCount: Math.max(Number(serverChat.unreadCount || 0), Number(mine.unreadCount || 0)),
            };
        }

        if (activeId && chatId === activeId && typeof document !== "undefined" && document.visibilityState === "visible") {
            next = { ...next, unreadCount: 0, missedCount: 0 };
        }

        return next;
    });
};

const initialState = {
    chats: [],
    loading: false,
    activeChatId: null,
    error: null,
    handledMessageIds: {},
};

const getId = (value) => String(value?._id || value || "");

const getSenderId = (message) => {
    return getId(message?.sender);
};

const getChatId = (message) => {
    return getId(message?.chatId);
};

const getMessagePreviewObject = (message) => ({
    _id: message._id,
    type: "message",
    messageKind: message.messageKind || "text",
    entity: message.entity || { type: "none" },
    text: message.text || "",
    fileName: message.fileName || null,
    fileUrl: message.fileUrl || null,
    fileType: message.fileType || null,
    fileMime: message.fileMime || null,
    sender: message.sender,
    senderId: getSenderId(message),
    createdAt: message.createdAt,
    readBy: message.readBy || [],
});

const trimHandled = (map = {}) => {
    const entries = Object.entries(map);

    if (entries.length <= 700) return map;

    return Object.fromEntries(entries.slice(entries.length - 700));
};

const upsertChat = (chats = [], chat) => {
    if (!chat?._id) return chats;

    const exists = chats.some((item) => String(item._id) === String(chat._id));

    const next = exists
        ? chats.map((item) => String(item._id) === String(chat._id) ? { ...item, ...chat } : item)
        : [chat, ...chats];

    next.sort((a, b) => (
        new Date(b.lastMessageAt || b.updatedAt || b.createdAt || 0) -
        new Date(a.lastMessageAt || a.updatedAt || a.createdAt || 0)
    ));

    return next;
};

export default function chatReducer(state = initialState, action) {
    switch (action.type) {
        case chatConstants.FETCH_CHATS_REQUEST:
        case chatConstants.FETCH_MY_CHATS_REQUEST:
        case chatConstants.CREATE_CHAT_REQUEST:
        case chatConstants.CREATE_DM_REQUEST:
        case chatConstants.CREATE_ROOM_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case chatConstants.FETCH_CHATS_SUCCESS:
        case chatConstants.FETCH_MY_CHATS_SUCCESS:
            return {
                ...state,
                chats: action.payload?.replace === false
                    ? (action.payload?.chats || []).reduce((acc, chat) => upsertChat(acc, chat), state.chats)
                    : mergeLoadedChats(state.chats, action.payload?.chats || [], state.activeChatId),
                loading: false,
                error: null,
            };

        case chatConstants.CREATE_CHAT_SUCCESS:
        case chatConstants.CREATE_DM_SUCCESS:
        case chatConstants.CREATE_ROOM_SUCCESS:
        case chatConstants.UPSERT_CHAT: {
            const chat = action.payload?.chat;

            if (!chat?._id) {
                return {
                    ...state,
                    loading: false,
                };
            }

            return {
                ...state,
                loading: false,
                error: null,
                chats: upsertChat(state.chats, chat),
                activeChatId: action.type === chatConstants.UPSERT_CHAT
                    ? state.activeChatId
                    : chat._id,
            };
        }

        case chatConstants.REMOVE_CHAT: {
            const chatId = getId(action.payload?.chatId || action.payload);

            if (!chatId) return state;

            return {
                ...state,
                chats: state.chats.filter((chat) => String(chat._id) !== chatId),
                activeChatId: String(state.activeChatId || "") === chatId ? null : state.activeChatId,
            };
        }

        case chatConstants.FETCH_CHATS_FAILURE:
        case chatConstants.FETCH_MY_CHATS_FAILURE:
        case chatConstants.CREATE_CHAT_FAILURE:
        case chatConstants.CREATE_DM_FAILURE:
        case chatConstants.CREATE_ROOM_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload?.error || "Ошибка загрузки чатов",
            };

        case chatConstants.SET_ACTIVE_CHAT_ID:
            return {
                ...state,
                activeChatId: action.payload?.chatId || null,
            };

        case messageConstants.RECEIVE_NEW_MESSAGE:
        case messageConstants.SEND_MESSAGE_SUCCESS:
        case messageConstants.SEND_TEXT_SUCCESS:
        case messageConstants.SEND_FILE_SUCCESS:
        case messageConstants.SEND_VOICE_SUCCESS: {
            const message = action.payload?.message;

            if (!message?.chatId || message?.__starPatch) return state;

            const chatId = getChatId(message);
            const messageId = getId(message._id);
            const meId = getId(action.payload?.meId);
            const mine = meId && getSenderId(message) === meId;
            const isActiveChat = String(state.activeChatId || "") === chatId;
            const isRealtime = action.type === messageConstants.RECEIVE_NEW_MESSAGE;
            const alreadyHandled = messageId ? !!state.handledMessageIds[messageId] : false;

            let found = false;

            const chats = state.chats.map((chat) => {
                if (String(chat._id) !== chatId) return chat;

                found = true;

                const unreadCount = Number(chat.unreadCount || 0);
                const lastMessageId = getId(chat.lastMessage?._id);
                const sameAsLast = messageId && lastMessageId === messageId;
                const shouldIncrementUnread = isRealtime && !alreadyHandled && !sameAsLast && !mine && (!isActiveChat || (typeof document !== "undefined" && document.visibilityState !== "visible"));

                return {
                    ...chat,
                    lastMessage: getMessagePreviewObject(message),
                    lastMessageAt: message.createdAt || new Date().toISOString(),
                    updatedAt: message.createdAt || new Date().toISOString(),
                    unreadCount: shouldIncrementUnread ? unreadCount + 1 : unreadCount,
                };
            });

            if (!found) return state;

            chats.sort((a, b) => (
                new Date(b.lastMessageAt || b.updatedAt || b.createdAt || 0) -
                new Date(a.lastMessageAt || a.updatedAt || a.createdAt || 0)
            ));

            return {
                ...state,
                chats,
                handledMessageIds: messageId
                    ? trimHandled({
                        ...state.handledMessageIds,
                        [messageId]: true,
                    })
                    : state.handledMessageIds,
            };
        }

        case 'MESSAGE_UPDATED_RECEIVED': {
            const incoming = action.payload?.message;
            const id = getId(incoming?._id);
            if (!id) return state;
            return {
                ...state,
                chats: state.chats.map((chat) => getId(chat.lastMessage?._id) === id
                    ? { ...chat, lastMessage: { ...chat.lastMessage, ...getMessagePreviewObject({ ...chat.lastMessage, ...incoming }) } }
                    : chat),
            };
        }

        case messageConstants.MARK_MESSAGES_AS_READ_SUCCESS: {
            const chatId = getId(action.payload?.chatId);
            const reader = getId(action.payload?.userId);
            if (!chatId || !reader) return state;
            const isSelfRead = action.payload?.isSelfRead !== false;
            return {
                ...state,
                chats: state.chats.map((chat) => {
                    if (getId(chat._id) !== chatId) return chat;
                    const last = chat.lastMessage;
                    const alreadyRead = (Array.isArray(last?.readBy) ? last.readBy : [])
                        .some((id) => getId(id) === reader);
                    return {
                        ...chat,
                        unreadCount: isSelfRead ? 0 : chat.unreadCount,
                        lastMessage: last && !alreadyRead ? {
                            ...last,
                            readBy: [...(Array.isArray(last.readBy) ? last.readBy : []), reader],
                        } : last,
                    };
                }),
            };
        }

        default:
            return state;
    }
}
